import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { ReactComponent as ViewIcon } from "../../../../assets/images/icons/view.svg";
import colors from "../../../../theme/color/ColorTheme";
import fontSize from "../../../../theme/fonts/FontTheme";
import { dateUtils } from "../../../../utils/DateUtils";
import StatusWrapper from "../components/StatusWrapper";

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  background: #fff;
  border-radius: 8px;
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  text-align: left;
`;
const TableHead = styled.thead`
  background-color: ${colors.backgroundTintSecondary};
`;
const Th = styled.th`
  padding: 16px;
  font-weight: 500;
  font-size: ${fontSize.s};
  color: #727272;

  &:nth-child(2) {
    width: 200px;
  }
`;
const TableBody = styled.tbody``;

const Td = styled.td`
  padding: 14px 16px;
  font-size: ${fontSize.s};
  border-bottom: 1px solid ${colors.dash};
  vertical-align: middle;

  &:first-child {
    font-weight: 500;
  }

  &:nth-child(2) {
    width: 200px;
  }
`;

const ActionIcons = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 8px;
  background: transparent;
  color: #555;
  font-weight: 500;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;

  background: #ddeaf8;
  color: #004181;
  border: 1.5px solid #004181;

  &:hover {
    background: #aacbec;
    color: #004181;
    border: 1.5px solid #004181;
  }
`;

const TableFooter = styled.tfoot`
  width: 100%;
`;
const FooterWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 14px;
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
const ArrowButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid #d6d6d6;
  background: transparent;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: background 0.2s;

  &:hover {
    background: #f0f0f0;
  }

  svg {
    width: 20px;
    height: 20px;
    stroke: #555;
  }
`;
const Numbers = styled.div`
  display: flex;
  align-items: center;
  gap: 2px;
`;
const PaginationButton = styled.button`
  min-width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0 12px;
  border-radius: 50%;
  transition: all 0.2s ease;
  font-weight: 600;
  font-size: ${fontSize.s};

  &:hover {
    background: #f0f0f0;
  }

  &.active {
    background: #004181;
    color: #fff;
    border-color: #004181;
  }
`;

const OrderTable = ({ orders, updatePage, page }) => {
  const navigate = useNavigate();

  const getPageNumbers = () => {
    const pages = [];
    if (page > 1) pages.push(page - 1);
    pages.push(page);
    pages.push(page + 1);
    return pages;
  };

  return (
    <TableWrapper>
      <StyledTable>
        <TableHead>
          <tr>
            <Th>Order ID</Th>
            <Th>Date</Th>
            <Th>Items</Th>
            <Th>Customer</Th>
            <Th>Total Price</Th>
            <Th>Completion Status</Th>
            <Th>Actions</Th>
          </tr>
        </TableHead>
        <TableBody>
          {orders.map((order) => (
            <tr key={order._id}>
              <Td>{order._id}</Td>
              <Td>{dateUtils.toCombinedDate(order.createdAt)}</Td>
              <Td>{order.items.length} items</Td>
              <Td>
                {order.customerDetails.firstName}{" "}
                {order.customerDetails.lastName}
              </Td>
              <Td>Rs.{order.totalAmount}</Td>
              <Td>
                <StatusWrapper status={order.status} />
              </Td>
              <Td>
                <ActionIcons
                  onClick={() => {
                    navigate(`/admin/order/${order._id}`);
                  }}
                >
                  <ViewIcon />
                  View
                </ActionIcons>
              </Td>
            </tr>
          ))}
          {orders && orders.length < 1 && (
            <tr>
              <div style={{ textAlign: "center", padding: "40px 0px" }}>
                No records found
              </div>
            </tr>
          )}
        </TableBody>
        <TableFooter>
          <tr>
            <Td colSpan={3} style={{ border: "none" }}>
              <FooterWrapper>
                <div>
                  Showing {orders.length} entries (Page {page})
                </div>
                <Pagination>
                  {/* Prev button */}
                  <ArrowButton
                    disabled={page === 1}
                    onClick={() => updatePage((p) => Math.max(p - 1, 1))}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 18L9 12L15 6"
                        stroke="#555"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ArrowButton>

                  <Numbers>
                    {getPageNumbers().map((p) => (
                      <PaginationButton
                        key={p}
                        className={page === p ? "active" : ""}
                        onClick={() => updatePage(p)}
                        disabled={orders.length < 10 && p > page}
                      >
                        {p}
                      </PaginationButton>
                    ))}
                  </Numbers>

                  <ArrowButton
                    disabled={orders.length < 10}
                    onClick={() => {
                      updatePage(page + 1);
                    }}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M9 18L15 12L9 6"
                        stroke="#555"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </ArrowButton>
                </Pagination>
              </FooterWrapper>
            </Td>
          </tr>
        </TableFooter>
      </StyledTable>
    </TableWrapper>
  );
};

export default OrderTable;
